import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { User, Building2, Save } from "lucide-react";
import { toast } from "react-toastify";
import { RoleBasedLayout } from "../../components/RoleBasedLayout";
import { useAppSelector, useAppDispatch } from "../../hooks";
import api from "../../lib/axios";
import { updateUser, updateBusiness } from "../../slices/authSlice";
import DragAndDropUpload from "../../components/DragAndDropUpload";
import ProtectedComponent from "../../components/ProtectedComponent";

const ProfileSchema = Yup.object({
  firstname: Yup.string().required("First name is required"),
  lastname: Yup.string().required("Last name is required"),
  othername: Yup.string(),
  username: Yup.string().required("Username is required"),
  email: Yup.string().email("Invalid email address").required("Email is required"),
  phone_number: Yup.string(),
});

const BusinessSchema = Yup.object({
  name: Yup.string().required("Business name is required"),
  website: Yup.string()
    .nullable()
    .test("website", "Enter a valid URL (e.g. https://example.com)", (val) => {
      if (!val) return true;
      try { new URL(val); return true; } catch { return false; }
    }),
  description: Yup.string(),
  logo: Yup.string(),
});

const SettingsPage = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const business = user?.Business;

  const initials = [user?.firstname?.[0], user?.lastname?.[0]]
    .filter(Boolean)
    .join("")
    .toUpperCase() || "?";

  const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(" ") || "No name set";

  const handleProfileSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const res = await api.put("/user/profile", values);
      dispatch(updateUser(res.data.data));
      toast.success("Profile updated successfully");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update profile");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBusinessSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const res = await api.put("/business/update", values);
      dispatch(updateBusiness(res.data.data));
      toast.success("Business updated successfully");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update business");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RoleBasedLayout>
      <div className="max-w-2xl mx-auto space-y-6 pb-10">

        {/* Personal Profile Card */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50 flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <User size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-800">Personal Profile</h2>
              <p className="text-xs text-gray-500">Update your personal information</p>
            </div>
          </div>

          <div className="p-6">
            {/* Avatar & identity summary */}
            <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
                {initials}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-gray-800 truncate">{fullName}</p>
                <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary font-medium capitalize">
                  {user?.main_role}
                </span>
              </div>
            </div>

            <Formik
              initialValues={{
                firstname: user?.firstname || "",
                lastname: user?.lastname || "",
                othername: user?.othername || "",
                username: user?.username || "",
                email: user?.email || "",
                phone_number: user?.phone_number || "",
              }}
              validationSchema={ProfileSchema}
              onSubmit={handleProfileSubmit}
              enableReinitialize
            >
              {({ errors, touched, isSubmitting }) => (
                <Form className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="form-label">
                    First Name <span className="text-red-500">*</span>
                    <Field
                      name="firstname"
                      className="form-input mt-1 w-full"
                      placeholder="First name"
                    />
                    {errors.firstname && touched.firstname && (
                      <p className="text-red-500 text-xs mt-1">{errors.firstname}</p>
                    )}
                  </label>

                  <label className="form-label">
                    Last Name <span className="text-red-500">*</span>
                    <Field
                      name="lastname"
                      className="form-input mt-1 w-full"
                      placeholder="Last name"
                    />
                    {errors.lastname && touched.lastname && (
                      <p className="text-red-500 text-xs mt-1">{errors.lastname}</p>
                    )}
                  </label>

                  <label className="form-label">
                    Other Name
                    <Field
                      name="othername"
                      className="form-input mt-1 w-full"
                      placeholder="Other name (optional)"
                    />
                  </label>

                  <label className="form-label">
                    Username <span className="text-red-500">*</span>
                    <Field
                      name="username"
                      className="form-input mt-1 w-full"
                      placeholder="username"
                    />
                    {errors.username && touched.username && (
                      <p className="text-red-500 text-xs mt-1">{errors.username}</p>
                    )}
                  </label>

                  <label className="form-label sm:col-span-2">
                    Email Address <span className="text-red-500">*</span>
                    <Field
                      name="email"
                      type="email"
                      className="form-input mt-1 w-full"
                      placeholder="email@example.com"
                    />
                    {errors.email && touched.email && (
                      <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                    )}
                  </label>

                  <label className="form-label sm:col-span-2">
                    Phone Number
                    <Field
                      name="phone_number"
                      className="form-input mt-1 w-full"
                      placeholder="+1 234 567 8900"
                    />
                  </label>

                  <div className="sm:col-span-2 flex justify-end">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex items-center gap-2 px-5 py-2 bg-primary text-white rounded-lg hover:bg-blue-900 disabled:opacity-70 transition-colors"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="loader"></span>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save size={16} />
                          <span>Save Profile</span>
                        </>
                      )}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>

        {/* Business Profile Card — Admin only */}
        <ProtectedComponent allowedRoles={["Admin"]}>
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b bg-gray-50 flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Building2 size={18} className="text-primary" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-800">Business Profile</h2>
                <p className="text-xs text-gray-500">Update your business details</p>
              </div>
            </div>

            <div className="p-6">
              <Formik
                initialValues={{
                  name: business?.name || "",
                  website: business?.website || "",
                  description: business?.description || "",
                  logo: business?.logo || "",
                }}
                validationSchema={BusinessSchema}
                onSubmit={handleBusinessSubmit}
                enableReinitialize
              >
                {({ errors, touched, isSubmitting }) => (
                  <Form className="grid grid-cols-1 gap-4">
                    <DragAndDropUpload name="logo" label="Business Logo" />

                    <label className="form-label">
                      Business Name <span className="text-red-500">*</span>
                      <Field
                        name="name"
                        className="form-input mt-1 w-full"
                        placeholder="Your business name"
                      />
                      {errors.name && touched.name && (
                        <p className="text-red-500 text-xs mt-1">{errors.name as string}</p>
                      )}
                    </label>

                    <label className="form-label">
                      Website
                      <Field
                        name="website"
                        className="form-input mt-1 w-full"
                        placeholder="https://example.com"
                      />
                      {errors.website && touched.website && (
                        <p className="text-red-500 text-xs mt-1">{errors.website as string}</p>
                      )}
                    </label>

                    <label className="form-label">
                      Description
                      <Field
                        as="textarea"
                        name="description"
                        className="form-input mt-1 w-full resize-none"
                        rows={3}
                        placeholder="A short description of your business"
                      />
                    </label>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex items-center gap-2 px-5 py-2 bg-primary text-white rounded-lg hover:bg-blue-900 disabled:opacity-70 transition-colors"
                      >
                        {isSubmitting ? (
                          <>
                            <span className="loader"></span>
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <Save size={16} />
                            <span>Save Business</span>
                          </>
                        )}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </ProtectedComponent>

      </div>
    </RoleBasedLayout>
  );
};

export default SettingsPage;
